import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  Badge as BadgeIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Percent as PercentIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import { number, object, string } from 'yup';
import { fetchConsignorForEdit, updateConsignor } from '../api/consignorDirectory';
import {
  DEFAULT_DIAL_CODE,
  formatE164,
  isValidNationalPhone,
  splitStoredPhone,
} from '../constants/phoneDialCodes';
import PhoneDialField from './PhoneDialField';

// ─── Styles ───────────────────────────────────────────────────────────────────

const ACCENT = '#000000';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: 'rgba(248, 250, 252, 1)',
    '& fieldset': { borderColor: 'rgba(219, 224, 230, 1)' },
    '&:hover fieldset': { borderColor: 'rgba(148, 163, 184, 0.7)' },
    '&.Mui-focused fieldset': { borderColor: '#111827' },
  },
};

const sectionLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.08em',
  color: 'text.disabled',
  mb: 1.5,
};

// ─── Validation ───────────────────────────────────────────────────────────────

const API_ERROR_ES: Record<string, string> = {
  'Phone already in use.': 'Ese teléfono ya está en uso.',
  'Email already in use.': 'Ese email ya está en uso.',
  'phone cannot be null.': 'El teléfono no puede quedar vacío.',
  'phone must be a valid +ACXXXXXXXXXX number.':
    'Usa un teléfono con código de país, ej. +523312345678.',
  'phone must be a non-empty string.': 'El teléfono no puede quedar vacío.',
  'email must be a valid email address.': 'Ingresa un correo válido.',
};

function friendlyError(message: string): string {
  return API_ERROR_ES[message] ?? message;
}

const schema = object({
  name: string().trim().required('El nombre es obligatorio'),
  email: string().trim().email('Correo inválido').required('El correo es obligatorio'),
  dialCode: string().required('Selecciona un código de país'),
  phone: string()
    .trim()
    .test('phone-optional', 'Ingresa un teléfono válido', (value, ctx) => {
      if (!value) return true;
      return isValidNationalPhone(ctx.parent.dialCode, value);
    }),
  commission: number()
    .min(0, 'Mínimo 0%')
    .max(100, 'Máximo 100%')
    .required('La comisión es obligatoria')
    .typeError('Debe ser un número'),
});

// ─── Props ────────────────────────────────────────────────────────────────────

export interface AdminEditConsignorModalProps {
  open: boolean;
  consignorId: number | null;
  onClose: () => void;
  onSaved: () => void;
  onError: (message: string) => void;
}

// ─── INE photo preview ────────────────────────────────────────────────────────

function InePhotoPreview({ url }: { url: string | null }): React.ReactElement {
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    setBroken(false);
  }, [url]);

  if (!url || broken) {
    return (
      <Box
        sx={{
          width: '100%',
          aspectRatio: '16/9',
          borderRadius: 2,
          border: '1px dashed rgba(203, 213, 225, 1)',
          bgcolor: 'rgba(248, 250, 252, 1)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          color: 'text.disabled',
        }}
      >
        <BadgeIcon sx={{ fontSize: 36 }} />
        <Typography variant="caption">
          {url ? 'No se pudo cargar la foto del INE' : 'Sin foto de INE registrada'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        aspectRatio: '16/9',
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid rgba(226, 232, 240, 1)',
        bgcolor: 'rgba(248, 250, 252, 1)',
        position: 'relative',
      }}
    >
      <Box
        component="img"
        src={url}
        alt="Foto de INE del consignador"
        onError={() => setBroken(true)}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block',
        }}
      />
    </Box>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────

export default function AdminEditConsignorModal({
  open,
  consignorId,
  onClose,
  onSaved,
  onError,
}: AdminEditConsignorModalProps): React.ReactElement {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [inePhotoUrl, setInePhotoUrl] = useState<string | null>(null);
  const [isActive, setIsActive] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const revealError = (message: string) => {
    const text = friendlyError(message);
    setSaveError(text);
    onError(text);
    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      dialCode: DEFAULT_DIAL_CODE,
      phone: '',
      commission: 30,
    },
    validationSchema: schema,
    onSubmit: async (values, helpers) => {
      if (!consignorId) return;
      setSaveError(null);
      const storedPhone = values.phone.trim() ? formatE164(values.dialCode, values.phone) : '';
      try {
        const result = await updateConsignor(consignorId, {
          name: values.name.trim(),
          email: values.email.trim(),
          ...(storedPhone ? { phone: storedPhone } : {}),
          commission: Number(values.commission),
          isActive,
          isAdmin,
        });
        if (!result.ok) {
          revealError(result.error);
          helpers.setSubmitting(false);
          return;
        }
        onSaved();
        onClose();
      } catch {
        revealError('No se pudo actualizar el consignador.');
        helpers.setSubmitting(false);
      }
    },
  });

  // Load data when modal opens
  useEffect(() => {
    let cancelled = false;
    if (open && consignorId != null) {
      setLoadingData(true);
      setLoadError(null);
      setSaveError(null);
      fetchConsignorForEdit(consignorId).then((result) => {
        if (cancelled) return;
        setLoadingData(false);
        if (!result.ok) {
          setLoadError(result.error);
          return;
        }
        const d = result.data;
        const parts = splitStoredPhone(d.phone);
        formik.resetForm({
          values: {
            name: d.name,
            email: d.email,
            dialCode: parts.dialCode,
            phone: parts.national,
            commission: d.commission,
          },
        });
        setInePhotoUrl(d.inePhotoUrl);
        setIsActive(d.isActive);
        setIsAdmin(d.isAdmin);
      });
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, consignorId]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setSaveError(null);
      setLoadError(null);
      setInePhotoUrl(null);
      formik.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    if (formik.isSubmitting) return;
    onClose();
  };

  const handleSaveClick = async () => {
    setSaveError(null);
    const errors = await formik.validateForm();
    await formik.setTouched({
      name: true,
      email: true,
      dialCode: true,
      phone: true,
      commission: true,
    });
    if (Object.keys(errors).length > 0) {
      revealError('Revisa los campos marcados.');
      return;
    }
    await formik.submitForm();
  };

  const isLoading = loadingData;
  const isSaving = formik.isSubmitting;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          fontWeight: 700,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Editar consignador
          </Typography>
          {consignorId != null && (
            <Typography variant="caption" color="text.disabled">
              ID #{consignorId}
            </Typography>
          )}
        </Box>
        <IconButton aria-label="Cerrar modal" onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent ref={contentRef} sx={{ pt: 2.5, pb: 1 }}>
        {saveError && !isLoading && (
          <Alert severity="error" onClose={() => setSaveError(null)} sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        )}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={32} sx={{ color: ACCENT }} />
          </Box>
        )}

        {!isLoading && loadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        )}

        {!isLoading && !loadError && (
          <Box component="form" noValidate onSubmit={formik.handleSubmit}>
            {/* ── Información personal ── */}
            <Typography sx={sectionLabelSx}>Información personal</Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              <TextField
                label="Nombre completo"
                name="name"
                fullWidth
                size="small"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                disabled={isSaving}
                sx={fieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Correo electrónico"
                name="email"
                type="email"
                fullWidth
                size="small"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                disabled={isSaving}
                sx={fieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                }}
              />

              <PhoneDialField
                id="edit-consignor-phone"
                label="Teléfono"
                compact
                dialCode={formik.values.dialCode}
                national={formik.values.phone}
                onDialCodeChange={(code) => formik.setFieldValue('dialCode', code)}
                onNationalChange={(digits) => formik.setFieldValue('phone', digits)}
                onBlur={() => formik.setFieldTouched('phone', true)}
                error={Boolean(formik.touched.phone && formik.errors.phone)}
                helperText={
                  formik.touched.phone && formik.errors.phone
                    ? formik.errors.phone
                    : 'Opcional — México +52 por defecto'
                }
                disabled={isSaving}
              />
            </Box>

            {/* ── Condiciones ── */}
            <Typography sx={sectionLabelSx}>Condiciones de consignación</Typography>

            <Box sx={{ mb: 3 }}>
              <TextField
                label="Comisión de Urban Utility"
                name="commission"
                type="number"
                size="small"
                value={formik.values.commission}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.commission && Boolean(formik.errors.commission)}
                helperText={
                  (formik.touched.commission && formik.errors.commission) ||
                  'Porcentaje que retiene la tienda (0–100)'
                }
                disabled={isSaving}
                sx={{ ...fieldSx, width: 270 }}
                onKeyDown={(e) => {
                  if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
                }}
                InputProps={{
                  inputProps: { min: 0, max: 100, step: 1 },
                  endAdornment: (
                    <InputAdornment position="end">
                      <PercentIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* ── Permisos ── */}
            <Typography sx={sectionLabelSx}>Permisos y estado</Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                mb: 3,
                p: 2,
                borderRadius: 2,
                border: '1px solid rgba(226, 232, 240, 1)',
                bgcolor: 'rgba(248, 250, 252, 1)',
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    disabled={isSaving}
                    size="small"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Cuenta activa
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      El consignador puede iniciar sesión y añadir inventario
                    </Typography>
                  </Box>
                }
              />
              <Divider sx={{ my: 0.5 }} />
              <Tooltip title="Otorga acceso completo al panel de administración">
                <FormControlLabel
                  control={
                    <Switch
                      checked={isAdmin}
                      onChange={(e) => setIsAdmin(e.target.checked)}
                      disabled={isSaving}
                      size="small"
                      color="warning"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <AdminIcon
                        fontSize="small"
                        sx={{ color: isAdmin ? '#F97316' : 'text.disabled' }}
                      />
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          Administrador
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          Acceso total al dashboard de administración
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
              </Tooltip>
            </Box>

            {/* ── INE photo ── */}
            <Typography sx={sectionLabelSx}>Foto de identificación (INE)</Typography>
            <InePhotoPreview url={inePhotoUrl} />
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: 'block', mt: 0.75, mb: 2 }}
            >
              La foto es subida por el consignador y no puede modificarse desde aquí.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={isSaving}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            handleSaveClick().catch(() => {});
          }}
          disabled={isLoading || isSaving || Boolean(loadError)}
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 700,
            bgcolor: ACCENT,
            '&:hover': { bgcolor: '#333' },
          }}
        >
          {isSaving ? 'Guardando…' : 'Guardar cambios'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
